import * as React from "react";
import useApi from "@/hooks/useApi";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { SectionCard } from "@/components/ds";

interface QnAPair {
  id: string;
  question: string;
  answer: string;
}

interface BotStatus {
  vectorEnabled: boolean;
  enableKbBotIndexing: boolean;
  hasAI: boolean;
}

interface Props {
  storeUrl?: string;
}

export default function MagentoBotKnowledge({ storeUrl }: Props) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [status, setStatus] = React.useState<BotStatus | null>(null);
  const [pairs, setPairs] = React.useState<QnAPair[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [scanLoading, setScanLoading] = React.useState(false);
  const [scanDone, setScanDone] = React.useState(false);
  const [savedCount, setSavedCount] = React.useState<number | null>(null);
  const [fetchingPolicies, setFetchingPolicies] = React.useState(false);
  const [policyFetchResult, setPolicyFetchResult] = React.useState<
    null | { count: number } | { error: string }
  >(null);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [statusResp, qnaResp] = await Promise.all([
          api.admin.get("MagentoBot/Status"),
          api.admin.get("MagentoBot/SuggestedQnA"),
        ]);
        const statusData = await statusResp.json();
        const qnaData = await qnaResp.json();
        setStatus(statusData ?? { vectorEnabled: false, enableKbBotIndexing: false, hasAI: false });
        if (Array.isArray(qnaData)) {
          setPairs(
            qnaData.map((t: any) => ({
              id: t.id ?? `pair_${Math.random()}`,
              question: t.question ?? "",
              answer: t.answer ?? "",
            }))
          );
        }
      } catch {
        setStatus({ vectorEnabled: false, enableKbBotIndexing: false, hasAI: false });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function scanStore() {
    if (!storeUrl) {
      toastAlert.displayToast(Severity.Warning, "Connect your Magento store first.");
      return;
    }
    setScanLoading(true);
    try {
      const resp = await api.admin.post("ScraperJobs/Create", {
        name: `Magento store \u2014 ${storeUrl}`,
        urls: [storeUrl.startsWith("http") ? storeUrl : `https://${storeUrl}`],
        maxPages: 100,
      });
      const data = await resp.json();
      if (data.allowed === false) {
        toastAlert.displayToast(Severity.Warning, data.reason || "Scan limit reached.");
      } else if (data.job_id) {
        setScanDone(true);
        toastAlert.displayToast(Severity.Success, `Scanning ${storeUrl} \u2014 this takes 2\u20133 minutes.`);
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to start scan.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to start scan.");
    } finally {
      setScanLoading(false);
    }
  }

  async function fetchPolicies() {
    const domain = storeUrl
      ? storeUrl.replace(/^https?:\/\//i, "").replace(/\/$/, "")
      : "";
    if (!domain) {
      setPolicyFetchResult({ error: "Connect your Magento store first." });
      return;
    }
    setFetchingPolicies(true);
    setPolicyFetchResult(null);
    try {
      const resp = await api.admin.get(
        `Magento/FetchPolicies?domain=${encodeURIComponent(domain)}`
      );
      const data = await resp.json();
      if (data.success && Array.isArray(data.pairs)) {
        let filled = 0;
        setPairs((prev) =>
          prev.map((existing) => {
            const match = (data.pairs as QnAPair[]).find((p) => p.id === existing.id);
            if (match && existing.answer.trim() === "" && match.answer.trim() !== "") {
              filled++;
              return { ...existing, answer: match.answer };
            }
            return existing;
          })
        );
        setPolicyFetchResult({ count: filled });
      } else {
        setPolicyFetchResult({
          error: data.message || "Could not reach your store policies.",
        });
      }
    } catch {
      setPolicyFetchResult({
        error: "Could not reach your store policies. Make sure your store is connected and try again.",
      });
    } finally {
      setFetchingPolicies(false);
    }
  }

  async function saveToBot() {
    const filledPairs = pairs.filter((p) => p.answer.trim().length > 0);
    if (filledPairs.length === 0) {
      toastAlert.displayToast(Severity.Warning, "Fill in at least one answer before saving.");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.admin.post(
        "MagentoBot/PushQnA",
        filledPairs.map((p) => ({ question: p.question, answer: p.answer }))
      );
      const data = await resp.json();
      if (data.success) {
        setSavedCount(data.pushed);
        toastAlert.displayToast(Severity.Success, data.message || `${data.pushed} pairs saved to bot.`);
      } else {
        toastAlert.displayToast(Severity.Error, data.message || "Failed to save.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save to bot.");
    } finally {
      setSaving(false);
    }
  }

  function addCustomPair() {
    setPairs((prev) => [...prev, { id: `custom_${Date.now()}`, question: "", answer: "" }]);
  }

  function removePair(id: string) {
    setPairs((prev) => prev.filter((p) => p.id !== id));
  }

  function updatePair(id: string, field: "question" | "answer", value: string) {
    setPairs((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const noAI = !status?.hasAI;
  const noVector = !status?.vectorEnabled;
  const filledCount = pairs.filter((p) => p.answer.trim().length > 0).length;
  const hasStore = Boolean(storeUrl);

  return (
    <div className="space-y-4">

      {/* Header */}
      <SectionCard title="Bot Knowledge" description="Teach your bot to answer the questions Magento B2B customers ask most. Fill in your store's answers and the bot will use them in every conversation — instantly, no workflow setup needed.">
        {noAI && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>AI not enabled on your plan.</strong> Upgrade to enable the bot knowledge feature.
          </div>
        )}
        {!noAI && noVector && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>Vector index not configured.</strong> Contact support to enable bot knowledge indexing.
          </div>
        )}
      </SectionCard>

      {/* Auto-fill from store */}
      <SectionCard title="Auto-fill from your store" description="Pull your return policy, shipping info, terms, and privacy policy directly from your Magento store pages — no copy/paste needed.">
        <button
          type="button"
          disabled={fetchingPolicies || !hasStore}
          onClick={fetchPolicies}
          className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            fetchingPolicies || !hasStore
              ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
              : "bg-zinc-800 text-white hover:bg-zinc-900"
          }`}
        >
          {fetchingPolicies ? "Fetching policies..." : "Fill from store \u2192"}
        </button>
      </SectionCard>

      {/* Step 1 - Store Scan */}
      <SectionCard title="Scan your store pages">
        <p className="text-sm text-zinc-500 mb-4">
          We'll crawl your Magento storefront and add your pages to the bot's knowledge base.
          This covers products, CMS pages, policies, and any content you've published. Takes 2\u20133
          minutes.{hasStore && <> Scanning: <strong>{storeUrl}</strong>.</>}
        </p>
        <button
          type="button"
          disabled={scanLoading || scanDone || !hasStore}
          onClick={scanStore}
          className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            scanDone
              ? "bg-green-100 text-green-700 cursor-default"
              : scanLoading
              ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
              : !hasStore
              ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {scanDone ? "Scan started" : scanLoading ? "Starting..." : "Scan my store"}
        </button>
        {scanDone && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
            Scan running in the background. Go to <strong>Chatbot Training</strong> to see progress
            and add the pages to your index.
          </div>
        )}
        {!hasStore && (
          <div className="mt-3 text-xs text-zinc-400">
            Connect your Magento store above to enable scanning.
          </div>
        )}
        <div className="mt-3 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
          <strong>What's indexed vs. live:</strong> This scan captures static pages — CMS pages, product descriptions, policies, and FAQs. It does <em>not</em> need to stay up to the minute.{" "}
          <strong>Stock levels, prices, orders, quotes, and cart data are always fetched live from Magento</strong> during the conversation via AI skills — never from this scan.{" "}
          To keep your scanned pages refreshed automatically, go to <strong>Chatbot Training → Web</strong> and enable auto-rebuild on a schedule.
        </div>
      </SectionCard>

      {/* Step 2 - QnA Builder */}
      <SectionCard title="Quick Q&A — your store's most common questions" description="Fill in your answers below. The bot will use these to answer customers instantly. Leave blank any questions that don't apply to your store.">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <span className="text-xs text-zinc-400">
              {filledCount}/{pairs.length} answered
            </span>
            <button
              type="button"
              disabled={saving || filledCount === 0}
              onClick={saveToBot}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                saving || filledCount === 0
                  ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {saving ? "Saving..." : "Save to bot"}
            </button>
          </div>
        </div>

        <div className="space-y-3 ml-8">
          {pairs.map((pair) => (
            <div key={pair.id} className="rounded-xl border border-zinc-200 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  {pair.id.startsWith("custom_") ? (
                    <input
                      type="text"
                      value={pair.question}
                      onChange={(e) => updatePair(pair.id, "question", e.target.value)}
                      placeholder="Type your question..."
                      className="w-full text-sm font-medium text-zinc-700 bg-transparent border-b border-zinc-200 pb-1 mb-2 focus:outline-none focus:border-blue-400"
                    />
                  ) : (
                    <div className="text-sm font-medium text-zinc-700 mb-2">{pair.question}</div>
                  )}
                  <textarea
                    rows={3}
                    value={pair.answer}
                    onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
                    placeholder="Type your answer here \u2014 be specific about your store's policy..."
                    className="w-full text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  />
                </div>
                {pair.id.startsWith("custom_") && (
                  <button
                    type="button"
                    onClick={() => removePair(pair.id)}
                    aria-label="Remove pair"
                    className="text-zinc-300 hover:text-red-400 mt-1 flex-shrink-0"
                  >
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addCustomPair}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add custom question
          </button>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <strong>Tip:</strong> Magento B2B customers often ask about requisition lists, negotiable quotes, company accounts,
          and tiered pricing. Be specific &mdash; B2B buyers expect precise answers. Instead of
          "Contact us", say "Our minimum order is 10 units. Volume pricing tiers start at 50 units."
        </div>
      </SectionCard>

      {/* Step 3 - Power Features */}
      <SectionCard title="Power features — beyond basic Q&A" description="Your bot can go far beyond answering policy questions. These AI skills let it search your product catalog, look up orders, check stock, manage quotes, and more — all in the chat window without any agent involvement.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { label: "Product Search", tool: "magento_search_products", desc: "Visitor asks about products \u2014 bot searches your catalog, shows pricing, availability, and SKUs." },
            { label: "Order Lookup", tool: "magento_get_order", desc: "Customer asks where their order is \u2014 bot retrieves live order status, items, and shipping info." },
            { label: "Customer Lookup", tool: "magento_get_customer", desc: "Bot finds the customer's Magento profile \u2014 order history, customer group, and account status." },
            { label: "Stock Check", tool: "magento_check_stock", desc: "Bot checks real-time inventory \u2014 answers 'is this in stock?' with urgency messaging when levels are low." },
            { label: "Cart Builder", tool: "magento_create_cart", desc: "Bot builds a cart with selected products and returns a checkout link \u2014 zero friction for customers." },
            { label: "Shipment Tracking", tool: "magento_get_tracking", desc: "Customer asks 'where is my package?' \u2014 bot retrieves carrier, tracking number, and a direct tracking link." },
            { label: "Product Variants", tool: "magento_get_configurable_options", desc: "Returns all configurable options (size, color) for a product with per-variant price and stock." },
            { label: "Apply Coupon", tool: "magento_apply_coupon", desc: "Bot applies a coupon code to the visitor's cart during the conversation to close hesitant buyers." },
            { label: "Abandoned Cart Recovery", tool: "magento_get_abandoned_carts", desc: "Find carts abandoned mid-checkout \u2014 powers cart recovery workflows." },
            { label: "Customer Group Pricing", tool: "magento_check_tier_pricing", desc: "Looks up tier/group-specific pricing for B2B accounts with wholesale or volume pricing." },
            { label: "Product Reviews", tool: "magento_get_reviews", desc: "Fetches customer reviews and star ratings from Magento's native review system." },
            { label: "Category Browse", tool: "magento_browse_category", desc: "Browse products by category \u2014 'show me all electronics' or 'what's in your sale category?'" },
            { label: "Requisition Lists", tool: "magento_get_requisition_list", desc: "B2B: Look up a customer's saved requisition lists for quick reorder (Adobe Commerce)." },
            { label: "Negotiable Quotes", tool: "magento_get_negotiable_quote", desc: "B2B: Check status of negotiable quotes \u2014 pending, approved, or countered (Adobe Commerce)." },
            { label: "Company Accounts", tool: "magento_get_company", desc: "B2B: Look up company account details, credit limits, and hierarchy (Adobe Commerce)." },
            { label: "Add Order Comment", tool: "magento_add_order_comment", desc: "Adds a status comment to an order \u2014 visible in admin order history." },
          ].map((f, i) => (
            <div key={i} className="rounded-xl border border-zinc-200 p-4 flex gap-3">
              <div className="text-blue-500 mt-0.5 flex-shrink-0">
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="2" />
                  <path d="M7 10l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <div className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                  {f.label}
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                    Professional
                  </span>
                  {(f.tool.includes("requisition") || f.tool.includes("negotiable") || f.tool.includes("company")) && (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                      B2B
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{f.desc}</div>
                <div className="text-xs text-zinc-400 mt-1 font-mono">{f.tool}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 bg-zinc-50 border border-zinc-200 rounded text-xs text-zinc-600">
          <strong>To enable:</strong> Go to <strong>Bots &rarr; AI Training &rarr; Skills</strong> and enable the
          Magento Commerce skills. These require a Professional plan and a connected Magento store.
          Then use the <strong>Launch Starter Wizard</strong> (Setup tab) to add a Magento workflow.
          16 tools available across both Open Source and Adobe Commerce B2B features.
        </div>
      </SectionCard>
    </div>
  );
}
